/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { SetStateAction } from 'react';

import { Subtenant } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedSubtenantsQuery } from '@/hooks/query/usePaginatedSubtenantsQuery';
import { LoadingSubtenants } from '@/pages/Subtenants/SubtenantHierarchy/LoadingSubtenants';
import { NoSubtenants } from '@/pages/Subtenants/SubtenantHierarchy/NoSubtenants';
import { useSubtenantListColumns } from '@/pages/Subtenants/useSubtenantListColumns';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import './SubtenantsHierarchy.css';

export type SubtenantHierarchyProps = {
  parentSubtenant?: Subtenant;
  editSubtenant: (subtenant: Subtenant) => void;
  setDeleteSubtenant: React.Dispatch<SetStateAction<Subtenant | undefined>>;
  nestingLevel?: number;
};

export function SubtenantsHierarchy({
  parentSubtenant,
  editSubtenant,
  setDeleteSubtenant,
  nestingLevel = 0,
}: SubtenantHierarchyProps) {
  const subtenantsQuery = usePaginatedSubtenantsQuery({
    type: parentSubtenant ? 'inMemory' : 'queryParams',
    params: { filters: { subtenantId: parentSubtenant?.id } },
  });
  const columns = useSubtenantListColumns(editSubtenant, setDeleteSubtenant);
  const setExpandedKeys = useMgmtPlaneStateStore((state) => state.setExpandedKeys);
  const expandedKeys = useMgmtPlaneStateStore((state) => state.expandedKeys);

  const loadingComponent = parentSubtenant ? <LoadingSubtenants /> : undefined;

  return (
    <div className={['subtenants-hierarchy', `nesting-level-${nestingLevel}`].join(' ')} style={{ width: '100%' }}>
      <QueryWrapper queryResult={subtenantsQuery} loadingComponent={loadingComponent}>
        {(subtenantsData) => {
          if (parentSubtenant && !subtenantsData.data.length) {
            return <NoSubtenants />;
          }

          return (
            <PaginatedTable
              data={subtenantsData}
              hidePaginationInfo={!!parentSubtenant}
              onParamsChange={subtenantsQuery.updateQueryParams}
              rowKey={(subtenant) => subtenant.id}
              columns={columns}
              data-testid={`subtenants-hierarchy-${parentSubtenant?.id || 'root'}`}
              expandable={{
                // we have to explicitly set this to something other than 'children' or undefined
                // otherwise AntD ignores the fact that we have a custom expandedRowRender
                // and duplicates the rows (shows the children as it usually does plus the expandedRowRender)
                childrenColumnName: 'not-children',
                rowExpandable: (record) => !!record.children,
                expandedRowKeys: expandedKeys,
                // eslint-disable-next-line react/no-unstable-nested-components
                expandedRowRender: (record: Subtenant, _idx: number, indent: number, expanded: boolean) => {
                  if (!expanded) return null;
                  return (
                    <SubtenantsHierarchy
                      parentSubtenant={record}
                      editSubtenant={editSubtenant}
                      setDeleteSubtenant={setDeleteSubtenant}
                      nestingLevel={nestingLevel + indent}
                    />
                  );
                },
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedKeys([...expandedKeys, record.id]);
                    return;
                  }
                  setExpandedKeys(expandedKeys.filter((key: string) => key !== record.id));
                },
              }}
            />
          );
        }}
      </QueryWrapper>
    </div>
  );
}
